/**
 * The FAQ page's Q&A content.
 *
 * <p>Fact-checked against this app's own Landing/Features/Roadmap pages and
 * its "status honesty" rule (see the disclaimer at the bottom of
 * `SiteFooter.tsx`): nothing here claims more than what those pages already
 * say is available today, building, or planned. Do not add, remove, or
 * reword an item without re-checking it against those pages first.
 */
export interface FaqItem {
  id: string
  q: string
  a: string
  category: string
}

export const FAQ_CATEGORIES = [
  'All',
  'General',
  'Hardware & Performance',
  'Models & Capabilities',
  'Privacy & Security',
  'Pricing & Billing',
  'Platform & Setup',
] as const

export const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'what-is-nevtan-ai',
    category: 'General',
    q: 'What is NevTan AI?',
    a: "NevTan AI is a local AI platform: install it, and it reads your computer's hardware, tells you which open AI models it can run well, and installs and runs them for you — locally, with answers generated on your own machine.",
  },
  {
    id: 'need-a-subscription',
    category: 'General',
    q: 'Do I need a subscription?',
    a: 'No. NevTan AI is free to use, with no subscription and nothing charged per message. If you choose to connect a hosted model with your own API key, that provider bills you directly for that usage — NevTan AI itself charges nothing.',
  },
  {
    id: 'which-os',
    category: 'General',
    q: 'Which operating systems does it run on?',
    a: 'NevTan AI is available today on macOS. Windows and Linux downloads are being built now.',
  },
  {
    id: 'how-decide-models',
    category: 'Hardware & Performance',
    q: 'How does NevTan AI decide which models I can run?',
    a: 'It reads your processor, memory, and free disk space, then tells you which models will run well before you download anything — matched primarily by system memory today.',
  },
  {
    id: 'uses-gpu',
    category: 'Hardware & Performance',
    q: 'Does it use my graphics card (GPU)?',
    a: 'NevTan AI detects your graphics card today, but does not yet use it to speed up models — GPU acceleration is being built now. For now, model recommendations are based on system memory.',
  },
  {
    id: 'how-install-model',
    category: 'Hardware & Performance',
    q: 'How do I install a model?',
    a: 'Open the app, see what your computer can run, pick a model for the task in front of you, and install it in one click. NevTan AI also keeps track of the model files, the storage they use, and their updates.',
  },
  {
    id: 'what-can-i-do-today',
    category: 'Models & Capabilities',
    q: 'What can I do with NevTan AI today?',
    a: "Chat with local models (streamed as they're written) and get help with coding, using models such as Qwen, Llama, Gemma, Mistral and Phi for chat, and Qwen Coder, DeepSeek Coder, Devstral and CodeGemma for coding.",
  },
  {
    id: 'read-own-documents',
    category: 'Models & Capabilities',
    q: 'Can it read my own documents or files?',
    a: 'Not yet. Answering questions from your own documents ("Knowledge") is planned, alongside reasoning, vision, document/OCR, image/audio/video generation, speech-to-text, text-to-speech, and translation — none of these are available today.',
  },
  {
    id: 'hosted-model-instead',
    category: 'Models & Capabilities',
    q: 'Can I use a hosted model instead of a local one?',
    a: 'Yes. NevTan AI supports hosted models using your own API key today, alongside the models it runs locally on your machine.',
  },
  {
    id: 'searchable-catalog',
    category: 'Models & Capabilities',
    q: 'Is there a searchable catalog of models?',
    a: 'A searchable model hub with categories and ratings is being built now; today the app surfaces a working set of chat and coding models it can run on your hardware.',
  },
  {
    id: 'where-conversations-stay',
    category: 'Privacy & Security',
    q: 'Where do my conversations and files stay?',
    a: "Local models run and answer entirely on your own computer — nothing is sent anywhere for that. If you connect a hosted model with your own API key, that conversation goes to whichever provider you chose, under that provider's own terms.",
  },
  {
    id: 'one-account',
    category: 'Privacy & Security',
    q: 'Is NevTan AI part of one account with the rest of NevTan?',
    a: 'Yes. One NevTan account signs you into NevTan AI along with NevTan Mail, Drive, Cloud, Sign, and Engage.',
  },
  {
    id: 'how-much-cost',
    category: 'Pricing & Billing',
    q: 'How much does NevTan AI cost?',
    a: 'NevTan AI is free to use. There is no subscription and nothing is charged per message for models it runs locally. Hosted models are billed by whichever provider issued the API key you connect.',
  },
  {
    id: 'buy-credits',
    category: 'Pricing & Billing',
    q: 'Do I need to buy credits for anything?',
    a: "No — local models carry no per-message or credit cost; you're only ever paying a third-party provider directly if you choose to connect a hosted model with your own key.",
  },
  {
    id: 'what-do-i-need',
    category: 'Platform & Setup',
    q: 'What do I need to install NevTan AI?',
    a: 'Download the macOS app, open it, and let it check your computer — no command line, no manually choosing file formats or setting memory limits; NevTan AI handles that setup for you.',
  },
  {
    id: 'finished-or-building',
    category: 'Platform & Setup',
    q: 'Is NevTan AI finished, or still being built?',
    a: 'NevTan AI is an actively developing platform. Every capability on the site shows its real status — Available today, Building, or Planned — and only what\'s marked "Available today" is in the version you can install right now.',
  },
]
